/**
 * Орлогын графикийн хугацааны муж, bucket-ийн хил.
 *
 * ⚠️ ЦАГИЙН БҮС БОЛ ЭНД ХАМГИЙН ЧУХАЛ ЗҮЙЛ.
 *    Дэлгүүр УБ-д ажиллана, сервер UTC-ээр цаг хадгална. `soldAt`-ыг
 *    UTC-ийн өдрөөр бүлэглэвэл өдрийн зааг УБ-ийн 08:00 цагт унана —
 *    өглөөний борлуулалт өмнөх өдөрт тоологдоно. Тиймээс хил бүрийг
 *    ЭНД, УБ-ийн хуанлиар тооцоод UTC агшин болгож буцаана.
 *
 * ⚠️ Offset-ыг `+08:00` гэж БИЧИХГҮЙ. Монгол 2017 оноос зуны цаг
 *    хэрэглэхээ больсон ч дахин нэвтрүүлбэл энэ файл чимээгүй буруу
 *    болно. Intl-ээс тухайн агшны offset-ыг уншина.
 *
 * ⚠️ Мөнгө энд ОРОХГҮЙ. Энэ файл зөвхөн хугацааны математик —
 *    дүнг Postgres `numeric` дээр нэмнэ (`decimal.js` биш, SQL).
 */
export const UB_TZ = 'Asia/Ulaanbaatar';
export const REVENUE_RANGES = ['day', 'week', 'month', 'year'];
export function isRevenueRange(v) {
    return typeof v === 'string' && REVENUE_RANGES.includes(v);
}
/** Bucket-ийн алхам — SQL `date_trunc`-ийн нэгжтэй тохирно. */
export const BUCKET_UNIT = {
    day: 'hour',
    week: 'day',
    month: 'day',
    year: 'month',
};
/**
 * Тухайн агшинд УБ хэдэн минутын offset-той байна вэ.
 * Ердийн үед 480 (+08:00).
 */
export function ulaanbaatarOffsetMinutes(at) {
    const name = new Intl.DateTimeFormat('en-US', {
        timeZone: UB_TZ,
        timeZoneName: 'longOffset',
    })
        .formatToParts(at)
        .find((p) => p.type === 'timeZoneName')?.value;
    const m = /GMT([+-])(\d{2}):(\d{2})/.exec(name ?? '');
    if (!m)
        return 480; // Intl гэнэт өөрчлөгдвөл УБ-ийн мэдэгдэж буй утга
    return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3]));
}
/** УБ-ийн хуанлиар тухайн агшны он-сар-өдөр-цаг. */
export function ulaanbaatarParts(at) {
    const p = new Intl.DateTimeFormat('en-CA', {
        timeZone: UB_TZ,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', hour12: false,
    }).formatToParts(at);
    const get = (t) => Number(p.find((x) => x.type === t)?.value ?? '0');
    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        // Зарим хөтөч шөнө дундыг "24" гэж буцаадаг
        hour: get('hour') % 24,
    };
}
/** УБ-ийн хуанлийн утгуудаас UTC агшин. */
export function fromUlaanbaatar(year, month, day, hour = 0) {
    // Эхлээд ойролцоо агшин барьж тэр үеийн offset-ыг олоод залруулна.
    // (Offset нь өөрөө огнооноос хамаардаг тул нэг алхам давтана.)
    const guess = new Date(Date.UTC(year, month - 1, day, hour, -480));
    const off = ulaanbaatarOffsetMinutes(guess);
    return new Date(Date.UTC(year, month - 1, day, hour, -off));
}
/**
 * Мужийн хил ба хүлээгдэж буй bucket-уудыг гаргана.
 *
 *   day   → сүүлийн 24 цаг, цагаар      (24 bucket)
 *   week  → сүүлийн 7 өдөр, өдрөөр      (7 bucket)
 *   month → тухайн сарын 1-ээс, өдрөөр  (28–31 bucket)
 *   year  → тухайн оны 1-р сараас, сараар (12 bucket)
 *
 * Bucket-ийн түлхүүр нь УБ-ийн орон нутгийн хуанлиар:
 *   hour  → "2026-08-24T14"
 *   day   → "2026-08-24"
 *   month → "2026-08"
 */
export function revenueWindow(range, now) {
    const p = ulaanbaatarParts(now);
    const unit = BUCKET_UNIT[range];
    const buckets = [];
    const pad = (n) => String(n).padStart(2, '0');
    if (range === 'day') {
        // Сүүлийн 24 цаг — одоогийн цагаас ухраад
        const end = fromUlaanbaatar(p.year, p.month, p.day, p.hour);
        const from = new Date(end.getTime() - 23 * 3_600_000);
        for (let i = 0; i < 24; i += 1) {
            const t = new Date(from.getTime() + i * 3_600_000);
            const q = ulaanbaatarParts(t);
            buckets.push(`${q.year}-${pad(q.month)}-${pad(q.day)}T${pad(q.hour)}`);
        }
        return { from, to: now, prevFrom: new Date(from.getTime() - 24 * 3_600_000), prevTo: from, unit, buckets };
    }
    if (range === 'week') {
        const today = fromUlaanbaatar(p.year, p.month, p.day);
        const from = new Date(today.getTime() - 6 * 86_400_000);
        for (let i = 0; i < 7; i += 1) {
            const q = ulaanbaatarParts(new Date(from.getTime() + i * 86_400_000 + 43_200_000));
            buckets.push(`${q.year}-${pad(q.month)}-${pad(q.day)}`);
        }
        return { from, to: now, prevFrom: new Date(from.getTime() - 7 * 86_400_000), prevTo: from, unit, buckets };
    }
    if (range === 'month') {
        const from = fromUlaanbaatar(p.year, p.month, 1);
        const days = new Date(Date.UTC(p.year, p.month, 0)).getUTCDate();
        for (let d = 1; d <= days; d += 1) {
            buckets.push(`${p.year}-${pad(p.month)}-${pad(d)}`);
        }
        const prevMonth = p.month === 1 ? 12 : p.month - 1;
        const prevYear = p.month === 1 ? p.year - 1 : p.year;
        return { from, to: now, prevFrom: fromUlaanbaatar(prevYear, prevMonth, 1), prevTo: from, unit, buckets };
    }
    const from = fromUlaanbaatar(p.year, 1, 1);
    for (let m = 1; m <= 12; m += 1)
        buckets.push(`${p.year}-${pad(m)}`);
    return { from, to: now, prevFrom: fromUlaanbaatar(p.year - 1, 1, 1), prevTo: from, unit, buckets };
}
const MONTH_MN = [
    '1-р', '2-р', '3-р', '4-р', '5-р', '6-р',
    '7-р', '8-р', '9-р', '10-р', '11-р', '12-р',
];
/**
 * Өрсөлдөөний хугацааны хил — УБ-ийн хуанлиар.
 *
 * `periodKey`:  month → "2026-08"   quarter → "2026-Q3"   year → "2026"
 *
 * ⚠️ Хил нь UTC агшин болж буцна — SQL нь `sold_at` дээр ШУУД
 *    харьцуулж индекс ашиглана. Хөрвүүлэлт нь энд, нэг газар.
 */
export function periodBounds(type, key) {
    const at = (y, m, d = 1) => fromUlaanbaatar(y, m, d);
    if (type === 'year') {
        const y = Number(key);
        return {
            from: at(y, 1), to: at(y + 1, 1),
            prevFrom: at(y - 1, 1), prevTo: at(y, 1),
            label: `${y} он`,
        };
    }
    if (type === 'quarter') {
        const [ys, qs] = key.split('-Q');
        const y = Number(ys);
        const q = Number(qs);
        const m = (q - 1) * 3 + 1;
        const prevQ = q === 1 ? 4 : q - 1;
        const prevY = q === 1 ? y - 1 : y;
        return {
            from: at(y, m), to: m + 3 > 12 ? at(y + 1, 1) : at(y, m + 3),
            prevFrom: at(prevY, (prevQ - 1) * 3 + 1),
            prevTo: at(y, m),
            label: `${y} оны ${q}-р улирал`,
        };
    }
    const [ys, ms] = key.split('-');
    const y = Number(ys);
    const m = Number(ms);
    const prevM = m === 1 ? 12 : m - 1;
    const prevY = m === 1 ? y - 1 : y;
    return {
        from: at(y, m), to: m === 12 ? at(y + 1, 1) : at(y, m + 1),
        prevFrom: at(prevY, prevM), prevTo: at(y, m),
        label: `${y} оны ${MONTH_MN[m - 1]} сар`,
    };
}
/** Одоогийн хугацааны түлхүүр — УБ-ийн хуанлиар. */
export function currentPeriodKey(type, now = new Date()) {
    const p = ulaanbaatarParts(now);
    if (type === 'year')
        return String(p.year);
    if (type === 'quarter')
        return `${p.year}-Q${Math.floor((p.month - 1) / 3) + 1}`;
    return `${p.year}-${String(p.month).padStart(2, '0')}`;
}
/** Тухайн хугацаа дууссан уу — дууссан бол snapshot хөлдөнө. */
export function isPeriodFinished(type, key, now = new Date()) {
    return periodBounds(type, key).to.getTime() <= now.getTime();
}
/**
 * УБ-ийн өнөөдрийн 00:00 агшин.
 *
 * `/reports/sales?from=` гэх мэт хилийг өөрсдөө заах эндпойнтод
 * хэрэглэнэ — тэдгээр нь `soldAt` дээр шууд харьцуулдаг тул үр дүн
 * тодорхой болно.
 */
export function ulaanbaatarDayStart(now = new Date()) {
    const p = ulaanbaatarParts(now);
    return fromUlaanbaatar(p.year, p.month, p.day);
}
/**
 * Агшны УБ-ийн огноо — `"2026-09-01"`.
 *
 * `toISOString().slice(0, 10)` нь UTC огноо өгдөг тул УБ-ийн 00:00–08:00
 * хоорондох агшин ӨМНӨХ өдөр болно. Тайлан өдрөөр бүлэглэхдээ үүнийг
 * хэрэглэнэ.
 */
export function ulaanbaatarDateKey(at) {
    const p = ulaanbaatarParts(at);
    const pad = (n) => String(n).padStart(2, '0');
    return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}
/**
 * Тухайн агшин аль bucket-д унах вэ — сервертэй ЯГ ижил түлхүүр.
 *
 * Клиент дээр optimistic update хийхэд хэрэгтэй: шинэ борлуулалт
 * ирэхэд бүх өгөгдлийг дахин татахгүйгээр зөв баганад нэмнэ.
 * Формат нь `revenueWindow().buckets`-тай тохирох ёстой.
 */
export function revenueBucketKey(range, at) {
    const p = ulaanbaatarParts(at);
    const pad = (n) => String(n).padStart(2, '0');
    switch (BUCKET_UNIT[range]) {
        case 'hour':
            return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}`;
        case 'month':
            return `${p.year}-${pad(p.month)}`;
        default:
            return ulaanbaatarDateKey(at);
    }
}
/**
 * Өсөлт/бууралтын хувь. Мөнгө string-ээр ирнэ (CLAUDE.md дүрэм 3).
 *
 * Өмнөх нь 0 бол хувь гаргах боломжгүй — `null` буцаана. «∞%» эсвэл
 * «100%» гэж харуулбал захирлыг төөрөгдүүлнэ.
 */
export function changePercent(current, previous) {
    const c = Number(current);
    const p = Number(previous);
    if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0)
        return null;
    return Math.round(((c - p) / p) * 1000) / 10;
}
// ------------------------------------------------------------
//  Үнийн бүс, долоо хоногийн өдөр — «юу их зарагддаг вэ»
// ------------------------------------------------------------
/**
 * Зарах үнийн бүс.
 *
 * ⚠️ Хил нь БИЗНЕСИЙН шийдвэр, техникийн биш. 2026 оны 9 сард
 *    бүртгэгдсэн гутлын үнэ 450,000–1,700,000₮ хооронд байсан тул
 *    бүсийг тэр орчимд 300,000₮-ийн алхамтайгаар тавив. Үнэ дээшилбэл
 *    энэ жагсаалтыг (ба тестийг нь) хамт өөрчилнө.
 * ⚠️ `max` нь ОРОХГҮЙ хязгаар. Сүүлийн бүс дээд хязгааргүй.
 */
export const PRICE_BANDS = [
    { key: 'b1', label: '500,000₮-аас доош', min: 0, max: 500_000 },
    { key: 'b2', label: '500–800 мянга', min: 500_000, max: 800_000 },
    { key: 'b3', label: '800 мянга – 1.1 сая', min: 800_000, max: 1_100_000 },
    { key: 'b4', label: '1.1–1.4 сая', min: 1_100_000, max: 1_400_000 },
    { key: 'b5', label: '1.4–1.7 сая', min: 1_400_000, max: 1_700_000 },
    { key: 'b6', label: '1.7 саяас дээш', min: 1_700_000, max: null },
];
/** Дүн аль бүсэд орох вэ. Буруу утга бол эхний бүс. */
export function priceBandOf(mnt) {
    const v = Number(mnt);
    if (!Number.isFinite(v))
        return { key: PRICE_BANDS[0].key, label: PRICE_BANDS[0].label };
    const band = PRICE_BANDS.find((b) => v >= b.min && (b.max === null || v < b.max));
    const hit = band ?? PRICE_BANDS[PRICE_BANDS.length - 1];
    return { key: hit.key, label: hit.label };
}
/** Долоо хоногийн өдрийн нэр — эрэмбэ нь Даваагаас эхэлнэ. */
export const WEEKDAY_MN = ['Ням', 'Даваа', 'Мягмар', 'Лхагва', 'Пүрэв', 'Баасан', 'Бямба'];
/**
 * `"2026-09-25"` → тухайн өдрийн нэр.
 *
 * ⚠️ Оролт нь УБ-ийн ХУАНЛИЙН өдөр (`ulaanbaatarDateKey`-ийн гаралт)
 *    тул энд цагийн бүс дахин тооцогдохгүй — UTC-ээр задалж болно.
 * ⚠️ Түлхүүр нь `1..7` (Даваа = 1): цагаан толгойн эрэмбэ нь гаригийн
 *    дарааллыг эвддэг («Бямба» эхэнд гарна).
 */
export function weekdayOfDateKey(key) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
    if (!m)
        return { key: '0', label: '—' };
    const dow = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
    return { key: String(dow === 0 ? 7 : dow), label: WEEKDAY_MN[dow] };
}
//# sourceMappingURL=revenue.js.map